import React, { useEffect, useState, useCallback, useRef } from 'react';
import EditorFull from '@/components/EditorFull';
import { useAuth } from '@/lib/auth-context';
import { usePassCodeur } from '@/lib/pass-codeur-context';
import { createProject, updateProject, getUserProjects, getProject, deleteProject } from '@/lib/projects-service';
import { useToast } from '@/lib/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog.jsx';
import { Label } from '@/components/ui/label';
import { useNavigate, useParams } from 'react-router-dom';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { ChevronDown, Pencil, Trash2, Globe, Lock, Share2, Crown } from 'lucide-react';
import AliasModal from '../components/AliasModal';
import AssetManager from '../components/AssetManager';
import { Switch } from '@/components/ui/switch';
import SEO from '../components/SEO';

// Code initial pour le bac à sable
const initialCode = `/**
 * @description Ajoute une description ici
 */
function setup() {
  createCanvas(600, 600);
}

function draw() {
  background(220);
}`;

const Projects = () => {
  const { currentUser, userData } = useAuth();
  const { hasAccessToCourses } = usePassCodeur();
  const { projectId, alias } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // État pour suivre l'onglet actif sur mobile (0 = Éditeur, 1 = Game)
  const [activeTab, setActiveTab] = useState(0);
  const [currentCode, setCurrentCode] = useState(initialCode);
  const [lastSavedCode, setLastSavedCode] = useState(initialCode);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [errorData, setErrorData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [showAliasModal, setShowAliasModal] = useState(false);
  const projectsRef = useRef(null);
  
  const editorGameRef = useRef(null);
  const editorEditorRef = useRef(null);
  const editorDesktopRef = useRef(null);
  
  const MAX_PROJECTS = 5;

  // Ajouter l'état pour la modal d'assets (avant la fonction return)
  const [showAssetsModal, setShowAssetsModal] = useState(false);

  // Fonction pour mettre à jour le code et vérifier les changements
  const handleCodeChange = (newCode) => {
    setCurrentCode(newCode);
    // Vérifier si le code a changé depuis la dernière sauvegarde
    setHasUnsavedChanges(newCode !== lastSavedCode);
  };

  // Fonction pour gérer le passage en public/privé d'un projet
  const handleTogglePublic = async (isPublic) => {
    if (!selectedProject) return;
    
    setIsSaving(true);
    try {
      // Mettre à jour le projet avec le nouveau statut public/privé
      await updateProject(selectedProject.id, { isPublic });
      
      // Mettre à jour l'état local
      setSelectedProject(prev => ({...prev, isPublic}));
      
      // Mettre à jour la liste des projets
      setProjects(prevProjects => 
        prevProjects.map(p => 
          p.id === selectedProject.id 
            ? { ...p, isPublic }
            : p
        )
      );
      
      toast({
        title: "Succès",
        description: isPublic 
          ? "Projet rendu public avec succès." 
          : "Projet rendu privé avec succès.",
        variant: "success",
      });
    } catch (error) {
      console.error("Erreur lors de la mise à jour du statut public:", error);
      toast({
        title: "Erreur",
        description: "Impossible de modifier le statut du projet.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Effet pour charger les projets au montage
  useEffect(() => {
    let isMounted = true;
    
    const loadProjects = async () => {
      // Si les projets sont déjà chargés ou en cours de chargement, ne pas recharger
              // Ou si l'utilisateur n'a pas le Pass Codeur ou n'est pas connecté
        if (!currentUser || projectsRef.current || !hasAccessToCourses()) return;
      
      try {
        const userProjects = await getUserProjects(currentUser.uid);
        if (isMounted) {
          projectsRef.current = true;
          setProjects(userProjects);
          
          // Si l'utilisateur n'a aucun projet, en créer un par défaut
          if (userProjects.length === 0) {
            try {
              const defaultProject = await createProject(currentUser.uid, "Nouveau projet", initialCode);
              setProjects([defaultProject]);
              setSelectedProject(defaultProject);
              setCurrentCode(defaultProject.code);
              if (userData?.alias) {
                navigate(`/project/${userData.alias}/${defaultProject.slug}`);
              }
            } catch (error) {
              console.error("Erreur lors de la création du projet par défaut:", error);
              toast({
                title: "Erreur",
                description: "Impossible de créer le projet par défaut.",
                variant: "destructive",
              });
            }
          }
        }
      } catch (error) {
        console.error("Erreur lors du chargement des projets:", error);
        if (isMounted) {
          toast({
            title: "Erreur",
            description: "Impossible de charger les projets.",
            variant: "destructive",
          });
        }
      }
    };

    loadProjects();
    
    return () => {
      isMounted = false;
    };
  }, [currentUser, toast, userData, navigate, hasAccessToCourses]);

  // Effet pour gérer la navigation et le chargement du projet sélectionné
  useEffect(() => {
    let isMounted = true;

    const loadSelectedProject = async () => {
      // Vérification de sécurité supplémentaire
      if (!currentUser || !projects.length || (alias && userData?.alias !== alias) || !hasAccessToCourses()) {
        return;
      }

      try {
        if (projectId) {
          // Éviter de recharger le projet si on a déjà les données complètes
          if (selectedProject?.slug === projectId && selectedProject?.code) {
            return;
          }

          // Toujours charger le projet complet depuis Firebase pour avoir le code à jour
          const project = await getProject(projectId, currentUser.uid);
          if (isMounted) {
            setSelectedProject(project);
            setCurrentCode(project.code);
            setLastSavedCode(project.code);
            setHasUnsavedChanges(false);
            setNewProjectName(project.name);
          }
        } else if (projects.length > 0) {
          // Si pas de projectId mais des projets existent, charger le plus récent
          const sortedProjects = [...projects].sort((a, b) => 
            new Date(b.updatedAt) - new Date(a.updatedAt)
          );
          const mostRecentProject = sortedProjects[0];
          
          // Éviter de recharger le projet si on a déjà les données complètes
          if (selectedProject?.id === mostRecentProject.id && selectedProject?.code) {
            return;
          }

          // Charger le code du projet le plus récent
          const fullProject = await getProject(mostRecentProject.slug, currentUser.uid);
          if (isMounted) {
            setSelectedProject(fullProject);
            setCurrentCode(fullProject.code);
            setLastSavedCode(fullProject.code);
            setHasUnsavedChanges(false);
            if (userData?.alias) {
              navigate(`/project/${userData.alias}/${mostRecentProject.slug}`);
            }
          }
        }
      } catch (error) {
        console.error("Erreur lors du chargement du projet:", error);
        if (isMounted && error.message === 'Projet non trouvé ou non autorisé') {
          toast({
            title: "Accès non autorisé",
            description: "Vous n'avez pas accès à ce projet.",
            variant: "destructive",
          });
          navigate(`/project/${userData?.alias || ''}`);
        } else if (isMounted) {
          toast({
            title: "Erreur",
            description: "Impossible de charger le projet.",
            variant: "destructive",
          });
        }
      }
    };

    loadSelectedProject();

    return () => {
      isMounted = false;
    };
  }, [currentUser, projectId, projects, userData, navigate, toast, alias, hasAccessToCourses]);

  // Effet pour vérifier si l'utilisateur a besoin de choisir un alias
  useEffect(() => {
    if (userData && !userData.alias && currentUser && hasAccessToCourses()) {
      setShowAliasModal(true);
    }
  }, [userData, currentUser, hasAccessToCourses]);

  // Si l'utilisateur essaie d'accéder à un autre profil
  useEffect(() => {
    if (userData?.alias && alias && userData.alias !== alias) {
      toast({
        title: "Accès non autorisé",
        description: "Vous ne pouvez accéder qu'à vos propres projets.",
        variant: "destructive",
      });
      navigate(`/project/${userData.alias}`);
    }
  }, [userData, alias, navigate, toast]);

  // Fonction pour sauvegarder le projet actuel sans modal
  const handleSave = async () => {
    if (!currentUser) {
      toast({
        title: "Compte requis",
        description: "Vous devez créer un compte ou vous connecter pour sauvegarder un projet.",
        variant: "destructive",
      });
      return;
    }
    setIsSaving(true);
    try {
      if (selectedProject) {
        // Mettre à jour le projet existant
        const updatedProject = await updateProject(selectedProject.id, {
          name: selectedProject.name,
          code: currentCode
        });
        // Mettre à jour la liste des projets localement
        setProjects(prevProjects => 
          prevProjects.map(p => 
            p.id === selectedProject.id 
              ? { ...p, code: currentCode, updatedAt: new Date().toISOString() }
              : p
          )
        );
        toast({
          title: "Succès",
          description: "Projet mis à jour avec succès.",
          variant: "success",
        });
        // Mettre à jour le code sauvegardé et réinitialiser l'état des modifications
        setLastSavedCode(currentCode);
        setHasUnsavedChanges(false);
      } else {
        // Créer un nouveau projet avec le nom courant ou "Nouveau projet"
        const name = newProjectName?.trim() ? newProjectName : "Nouveau projet";
        const newProject = await createProject(currentUser.uid, name, currentCode);
        // Ajouter le nouveau projet à la liste locale
        setProjects(prevProjects => [newProject, ...prevProjects]);
        toast({
          title: "Succès",
          description: "Projet créé avec succès.",
          variant: "success",
        });
        navigate(`/project/${userData.alias}/${newProject.slug}`);
        // Mettre à jour le code sauvegardé et réinitialiser l'état des modifications
        setLastSavedCode(currentCode);
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder le projet.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  // Fonction pour basculer vers l'onglet Game (utilisée lorsque l'utilisateur appuie sur Play)
  const switchToGameTab = useCallback(() => {
    setActiveTab(1);
  }, []);
  
  // Fonction pour lancer le jeu
  const runGame = useCallback(() => {
    // Sauvegarder automatiquement avant de lancer le jeu si des modifications existent
    if (hasUnsavedChanges && currentUser && selectedProject) {
      handleSave();
    }
    
    if (editorGameRef.current) {
      editorGameRef.current.runSketch();
    }
  }, [hasUnsavedChanges, currentUser, selectedProject, handleSave]);

  // Effet pour lancer le jeu lorsque l'onglet Game est sélectionné
  // et l'arrêter quand on revient à l'onglet Éditeur
  useEffect(() => {
    if (activeTab === 1) {
      // Petit délai pour s'assurer que le composant est monté
      const timer = setTimeout(() => {
        runGame();
      }, 100);
      
      return () => clearTimeout(timer);
    } else {
      // Si on revient sur l'onglet Éditeur, arrêter le sketch
      if (editorGameRef.current) {
        // Accès à la méthode cleanupSketch exposée via useImperativeHandle
        editorGameRef.current.runSketch && editorGameRef.current.cleanupSketch && editorGameRef.current.cleanupSketch();
      }
    }
  }, [activeTab, runGame]);
  
  // Désactiver le défilement lorsque la page est montée
  useEffect(() => {
    // Sauvegarder le style de défilement actuel
    const originalStyle = document.body.style.overflow;
    
    // Désactiver le défilement
    document.body.style.overflow = 'hidden';
    
    // Rétablir le style original lors du démontage du composant
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);
  
  // Effet pour détecter quand l'utilisateur quitte la page Project
  useEffect(() => {
    const handleLeavePage = () => {
      // Sauvegarder automatiquement si des modifications non sauvegardées existent
      if (hasUnsavedChanges && currentUser && selectedProject) {
        //console.log("Sauvegarde automatique du projet avant navigation");
        updateProject(selectedProject.id, {
          name: selectedProject.name,
          code: currentCode
        }).then(() => {
          //console.log("Projet sauvegardé automatiquement");
          setLastSavedCode(currentCode);
          setHasUnsavedChanges(false);
        }).catch(error => {
          console.error("Erreur lors de la sauvegarde automatique:", error);
        });
      }
    };
    
    // Pour la navigation externe (fermeture de l'onglet, autre site)
    const handleBeforeUnload = (e) => {
      // Sauvegarder automatiquement si possible
      if (hasUnsavedChanges && currentUser && selectedProject) {
        // Essayer de sauvegarder, mais notez que dans beforeunload,
        // la sauvegarde peut ne pas avoir le temps de se terminer
        updateProject(selectedProject.id, {
          name: selectedProject.name,
          code: currentCode
        }).catch(error => {
          console.error("Erreur lors de la tentative de sauvegarde avant déchargement:", error);
        });
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Pour la navigation interne (se déclenchera lors du démontage du composant)
    return () => {
      // Cette fonction se déclenche uniquement pour la navigation interne
      if (window.location.pathname !== `/project/${userData?.alias}/${selectedProject?.slug}`) {
        handleLeavePage();
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentUser, currentCode, selectedProject, userData, hasUnsavedChanges, lastSavedCode]);

  // Sauvegarder le projet
  const saveProject = async () => {
    if (!currentUser) {
      toast({
        title: "Erreur",
        description: "Vous devez être connecté pour sauvegarder un projet.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      if (projectId) {
        // Mettre à jour le projet existant
        await updateProject(projectId, {
          name: newProjectName,
          code: currentCode
        });
        toast({
          title: "Succès",
          description: "Projet sauvegardé avec succès.",
          variant: "success",
        });
      } else {
        // Créer un nouveau projet
        const newProject = await createProject(currentUser.uid, newProjectName, currentCode);
        toast({
          title: "Succès",
          description: "Projet créé avec succès.",
          variant: "success",
        });
        navigate(`/project/${userData.alias}/${newProject.slug}`);
      }
      setShowSaveDialog(false);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder le projet.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Fonction pour supprimer un projet
  const handleDeleteProject = async (projectId) => {
    try {
      await deleteProject(projectId);
      // Mettre à jour la liste des projets localement
      setProjects(prevProjects => prevProjects.filter(p => p.id !== projectId));
      toast({
        title: "Succès",
        description: "Projet supprimé avec succès.",
        variant: "success",
      });
      if (projectId === selectedProject?.id) {
        navigate('/project');
        setCurrentCode(initialCode);
        setSelectedProject(null);
      }
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le projet.",
        variant: "destructive",
      });
    }
  };

  // Fonction pour éditer le nom d'un projet
  const handleEditProject = async () => {
    setIsSaving(true);
    try {
      if (selectedProject) {
        // Mise à jour d'un projet existant
        const updatedProject = await updateProject(selectedProject.id, {
          name: newProjectName,
          code: currentCode
        });
        setSelectedProject({ ...selectedProject, name: newProjectName, slug: updatedProject.slug });
        // Mettre à jour la liste des projets localement
        setProjects(prevProjects => 
          prevProjects.map(p => 
            p.id === selectedProject.id 
              ? { ...p, name: newProjectName, code: currentCode, slug: updatedProject.slug, updatedAt: new Date().toISOString() }
              : p
          )
        );
        toast({
          title: "Succès",
          description: "Projet mis à jour avec succès.",
          variant: "success",
        });
        // Rediriger vers la nouvelle URL si le nom a changé
        navigate(`/project/${userData.alias}/${updatedProject.slug}`);
      } else {
        // Vérifier la limite de projets
        if (projects.length >= MAX_PROJECTS) {
          toast({
            title: "Limite atteinte",
            description: `Vous ne pouvez pas créer plus de ${MAX_PROJECTS} projets. Supprimez un projet existant pour en créer un nouveau.`,
            variant: "destructive",
          });
          setShowEditDialog(false);
          setIsSaving(false);
          return;
        }
        // Création d'un nouveau projet
        const newProject = await createProject(currentUser.uid, newProjectName, currentCode);
        // Ajouter le nouveau projet à la liste locale
        setProjects(prevProjects => [newProject, ...prevProjects]);
        toast({
          title: "Succès",
          description: "Projet créé avec succès.",
          variant: "success",
        });
        navigate(`/project/${userData.alias}/${newProject.slug}`);
      }
      setShowEditDialog(false);
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le projet.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Mode desktop par défaut
  return (
    <>
      <SEO 
        title="Éditeur de projets - Créez vos jeux vidéo - Mon Premier Jeu"
        description="Créez, modifiez et partagez vos jeux vidéo avec notre éditeur intégré. Sauvegardez vos projets et partagez-les avec la communauté."
        keywords="éditeur jeu, créer jeu vidéo, projets programmation, p5.js éditeur, partage jeux, code en ligne"
        url="/project"
        type="webapp"
      />
      <AliasModal 
        isOpen={showAliasModal} 
        redirectOnSuccess={false}
        onClose={(alias) => {
          setShowAliasModal(false);
          if (alias) {
            // Si un alias a été fourni (succès), rester sur Projects
            navigate(`/project/${alias}`);
          } else {
            // Si aucun alias fourni (fermeture sans succès), rediriger vers l'accueil
            navigate('/');
          }
        }} 
      />
      <div className="fixed inset-0 pt-16 flex flex-col">
        {/* Barre de navigation des projets */}
        <div className="px-4 py-2 flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="h-8 px-2">
                {selectedProject ? selectedProject.name : "Nouveau projet"}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {projects
                .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
                .map((project) => (
                  <DropdownMenuItem
                    key={project.id}
                    onClick={async () => {
                      // Arrêter le sketch en cours si il y en a un
                      if (editorGameRef.current) {
                        editorGameRef.current.cleanupSketch && editorGameRef.current.cleanupSketch();
                      }
                      if (editorEditorRef.current) {
                        editorEditorRef.current.cleanupSketch && editorEditorRef.current.cleanupSketch();
                      }
                      if (editorDesktopRef.current) {
                        editorDesktopRef.current.cleanupSketch && editorDesktopRef.current.cleanupSketch();
                      }
                      
                      // Revenir à l'onglet Éditeur
                      setActiveTab(0);
                      
                      // Mettre à jour la date de modification du projet
                      await updateProject(project.id, {
                        updatedAt: new Date().toISOString()
                      });
                      // Mettre à jour la liste des projets localement
                      setProjects(prevProjects => 
                        prevProjects.map(p => 
                          p.id === project.id 
                            ? { ...p, updatedAt: new Date().toISOString() }
                            : p
                        )
                      );
                      navigate(`/project/${userData.alias}/${project.slug}`);
                    }}
                  >
                    {project.name}
                  </DropdownMenuItem>
                ))}
              {projects.length > 0 && (
                <div className="h-px bg-border my-1 w-full" />
              )}
              {projects.length < MAX_PROJECTS ? (
                <DropdownMenuItem 
                  onClick={() => {
                    // Arrêter le sketch en cours si il y en a un
                    if (editorGameRef.current) {
                      editorGameRef.current.cleanupSketch && editorGameRef.current.cleanupSketch();
                    }
                    if (editorEditorRef.current) {
                      editorEditorRef.current.cleanupSketch && editorEditorRef.current.cleanupSketch();
                    }
                    if (editorDesktopRef.current) {
                      editorDesktopRef.current.cleanupSketch && editorDesktopRef.current.cleanupSketch();
                    }
                    
                    // Revenir à l'onglet Éditeur
                    setActiveTab(0);
                    
                    setSelectedProject(null);
                    setCurrentCode(initialCode);
                    setNewProjectName('Nouveau projet');
                    setShowEditDialog(true);
                  }}
                >
                  Créer un nouveau projet
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem className="text-muted-foreground" disabled>
                  Limite de {MAX_PROJECTS} projets atteinte
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Bouton d'édition - toujours visible */}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => {
              setNewProjectName(selectedProject ? selectedProject.name : 'Nouveau projet');
              setShowEditDialog(true);
            }}
          >
            <Pencil className="h-4 w-4" />
          </Button>

          {/* Bouton de suppression - visible uniquement si un projet est sélectionné */}
          {selectedProject && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive"
              onClick={() => {
                setProjectToDelete(selectedProject);
                setShowDeleteAlert(true);
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}

          {/* Control pour rendre le projet public/privé */}
          {userData?.alias && selectedProject && (
            <div className="flex items-center ml-auto">
              <div className="flex items-center space-x-2 mr-2">
                <Switch
                  id="project-visibility"
                  checked={selectedProject.isPublic || false}
                  onCheckedChange={handleTogglePublic}
                  aria-label="Visibilité du projet"
                />
                <Label htmlFor="project-visibility" className="cursor-pointer flex items-center gap-1">
                  {selectedProject.isPublic ? (
                    <>
                      <Globe className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Public</span>
                    </>
                  ) : (
                    <>
                      <Lock className="h-4 w-4" />
                      <span className="text-sm">Privé</span>
                    </>
                  )}
                </Label>
              </div>
              
              {selectedProject.isPublic && userData.alias && (
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => {
                    // Copier le lien de partage dans le presse-papier
                    const shareUrl = `${window.location.origin}/play/${userData.alias}/${selectedProject.slug}`;
                    navigator.clipboard.writeText(shareUrl);
                    toast({
                      title: "Lien copié !",
                      description: "Le lien de partage a été copié dans le presse-papier.",
                      variant: "success",
                    });
                  }}
                  title="Partager le projet"
                >
                  <Share2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Contenu principal */}
        <div className="flex-1 px-4 pb-4 flex flex-col">
          {/* Navigation par onglets - Visible uniquement sur mobile */}
          <div className="md:hidden mb-4 flex rounded-full bg-neutral-100 dark:bg-gray-800 p-1 w-full max-w-md mx-auto">
            <button
              onClick={() => setActiveTab(0)}
              className={`flex-1 rounded-full py-2 px-3 text-center text-sm font-medium ${
                activeTab === 0
                  ? 'bg-indigo-500 text-white shadow'
                  : 'text-neutral-600 dark:text-gray-300 hover:bg-neutral-300 dark:hover:bg-gray-700 hover:text-neutral-900 dark:hover:text-white'
              }`}
            >
              Éditeur
            </button>
            <button
              onClick={() => setActiveTab(1)}
              className={`flex-1 rounded-full py-2 px-3 text-center text-sm font-medium ${
                activeTab === 1
                  ? 'bg-indigo-500 text-white shadow'
                  : 'text-neutral-600 dark:text-gray-300 hover:bg-neutral-300 dark:hover:bg-gray-700 hover:text-neutral-900 dark:hover:text-white'
              }`}
            >
              Game
            </button>
          </div>
          
          {/* Contenu desktop - caché sur mobile */}
          <div className="hidden md:block flex-1 overflow-hidden">
            <EditorFull 
              initialCode={currentCode} 
              onCodeChange={handleCodeChange}
              onError={setErrorData}
              onSave={handleSave}
              onAssetsClick={() => setShowAssetsModal(true)}
              workdir={currentUser?.uid+"/"+selectedProject?.id}
              ref={editorDesktopRef}
            />
          </div>
          
          {/* Contenu mobile - visible uniquement sur mobile */}
          <div className="md:hidden flex-1 overflow-hidden">
            {/* Toujours rendre les deux composants mais en cacher un selon l'onglet actif */}
            <div className="h-full w-full" style={{ display: activeTab === 0 ? 'block' : 'none' }}>
              <div className="rounded-lg overflow-hidden h-full">
                <EditorFull 
                  initialCode={currentCode} 
                  mobileMode="editor" 
                  onPlayClick={switchToGameTab}
                  onCodeChange={handleCodeChange}
                  onError={setErrorData}
                  onSave={handleSave}
                  onAssetsClick={() => setShowAssetsModal(true)}
                  ref={editorEditorRef}
                  workdir={currentUser?.uid+"/"+selectedProject?.id}
                />
              </div>
            </div>
            
            <div className="h-full w-full" style={{ display: activeTab === 1 ? 'block' : 'none' }}>
              <div className="rounded-lg overflow-hidden h-full">
                <EditorFull 
                  initialCode={currentCode} 
                  mobileMode="canvas"
                  onError={setErrorData}
                  ref={editorGameRef}
                  workdir={currentUser?.uid+"/"+selectedProject?.id}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Dialog pour sauvegarder un nouveau projet */}
        <Dialog open={showSaveDialog && currentUser && hasAccessToCourses()} onOpenChange={setShowSaveDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouveau projet</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="projectName">Nom du projet</Label>
                <Input
                  id="projectName"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="Entrez le nom du projet"
                />
              </div>
              <Button 
                onClick={saveProject} 
                disabled={isSaving || !newProjectName.trim()}
              >
                {isSaving ? "Sauvegarde..." : "Sauvegarder"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog pour éditer un projet */}
        <Dialog open={showEditDialog && currentUser && hasAccessToCourses()} onOpenChange={(open) => {
          setShowEditDialog(open);
          if (!open && !selectedProject && projects.length > 0) {
            // Revenir sur le projet le plus récent
            const sorted = [...projects].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
            const mostRecent = sorted[0];
            setSelectedProject(mostRecent);
            setCurrentCode(mostRecent.code);
            navigate(`/project/${userData.alias}/${mostRecent.slug}`);
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {selectedProject ? "Modifier le projet" : "Renommer le projet"}
              </DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="editProjectName">Nom du projet</Label>
                <Input
                  id="editProjectName"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="Entrez le nom du projet"
                />
              </div>
              <Button 
                onClick={handleEditProject} 
                disabled={isSaving || !newProjectName.trim()}
              >
                {isSaving ? "Enregistrement..." : "OK"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog de confirmation de suppression */}
        <AlertDialog open={showDeleteAlert && currentUser && hasAccessToCourses()} onOpenChange={setShowDeleteAlert}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Êtes-vous sûr ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. Le projet "{projectToDelete?.name}" sera définitivement supprimé.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (projectToDelete) {
                    handleDeleteProject(projectToDelete.id);
                  }
                  setShowDeleteAlert(false);
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Modal Assets */}
        <Dialog open={showAssetsModal && currentUser && hasAccessToCourses()} onOpenChange={setShowAssetsModal}>
          <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Gestion des Assets</DialogTitle>
              <p className="text-xs text-muted-foreground mb-2">Ajoute une image icon.jpg (512x256) pour personaliser l'icône de ton jeu</p>
            </DialogHeader>
            {selectedProject && (
              <AssetManager projectId={selectedProject.id} />
            )}
          </DialogContent>
        </Dialog>

        {/* Overlay compte requis - affiché par-dessus tout le contenu */}
        {!currentUser && (
                      <div className="fixed inset-0 bg-background/50 backdrop-blur-xs z-50 flex items-center justify-center">
            <div className="border border-border rounded-lg p-8 bg-card max-w-md mx-4 shadow-lg">
              <div className="flex flex-col items-center gap-6">
                <div className="text-center">
                  <h3 className="text-2xl font-semibold mb-2">Compte requis</h3>
                  <p className="text-muted-foreground">
                    Vous devez créer un compte gratuit ou vous connecter pour créer vos propres projets.
                  </p>
                </div>
                <Button 
                  onClick={() => navigate('/login')}
                  className="pass-codeur-btn"
                  size="lg"
                >
                  Créer un compte / Se connecter
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Projects; 